import { sendGTMEvent } from '@next/third-parties/google'

export enum GTM_EVENT_CATEGORY {
  DONATION = 'DONATION',
  PRODUCT = 'PRODUCT',
  NEWSLETTER = 'NEWSLETTER',
  VENDOR_CLICK = 'VENDOR_CLICK',
  DISTRIBUTION = 'DISTRIBUTION',
  QUERY_LOCATION = 'QUERY_LOCATION',
  LOCATION_ACTIONS = 'LOCATION_ACTIONS',
  // CTA_CLICK = 'CTA_CLICK',
  EVENT_CLICKTHROUGH = 'EVENT_CLICKTHROUGH',
  HERO_DONATION = 'HERO_DONATION',
  PARTNER_CLICKTHROUGH = 'PARTNER_CLICKTHROUGH',
  SUPPORT_US = 'SUPPORT_US',
}

export enum GTM_EVENT_SECTION {
  DONA_CTA_HERO = 'Sostieni Hero',
  DONA_CTA_HEADER = 'DonaCTAHeader',
  DONA_CTA_FOOTER = 'DonaCTAFooter',
}

type GTMEventBase = {
  category: GTM_EVENT_CATEGORY
  event: string
  eventName: string
}

export type GTMEventWithSection = GTMEventBase & {
  section: string
}

export type GTMEventWithItemData = GTMEventBase & {
  cta_clicked: string
}

export type GTMEventWithCTAname = GTMEventBase & {
  buttonName: string
}

type GTMEventMap = {
  [GTM_EVENT_CATEGORY.DONATION]: GTMEventWithSection
  [GTM_EVENT_CATEGORY.PRODUCT]: GTMEventWithItemData
  [GTM_EVENT_CATEGORY.NEWSLETTER]: GTMEventBase
  [GTM_EVENT_CATEGORY.QUERY_LOCATION]: GTMEventWithSection
  [GTM_EVENT_CATEGORY.VENDOR_CLICK]: GTMEventWithSection
  [GTM_EVENT_CATEGORY.DISTRIBUTION]: GTMEventWithCTAname
  [GTM_EVENT_CATEGORY.LOCATION_ACTIONS]: GTMEventWithSection
  [GTM_EVENT_CATEGORY.EVENT_CLICKTHROUGH]: GTMEventWithSection
  [GTM_EVENT_CATEGORY.HERO_DONATION]: GTMEventWithSection
  [GTM_EVENT_CATEGORY.PARTNER_CLICKTHROUGH]: GTMEventWithSection
  [GTM_EVENT_CATEGORY.SUPPORT_US]: GTMEventWithSection
  // [GTM_EVENT_CATEGORY.CTA_CLICK]: GTMEventWithItemData
}

const GTM_CONFIG = {
  [GTM_EVENT_CATEGORY.DONATION]: {
    event: 'donaButton',
    eventName: `dona_ora_nastrorosa`,
  },

  [GTM_EVENT_CATEGORY.HERO_DONATION]: {
    event: 'sostieniRicercaBtn',
    eventName: `sostieni_nastrorosa`,
  },

  [GTM_EVENT_CATEGORY.PRODUCT]: {
    event: `product_view`,
    eventName: `product_view`,
  },

  [GTM_EVENT_CATEGORY.NEWSLETTER]: {
    event: 'nrNewsletter',
    eventName: `newslettersignup_nastrorosa`,
  },

  [GTM_EVENT_CATEGORY.QUERY_LOCATION]: {
    event: 'ricercaPiazza',
    eventName: 'ricerca_piazza_nastro_rosa',
  },

  // TODO support multiple vendors
  [GTM_EVENT_CATEGORY.VENDOR_CLICK]: {
    event: 'amazonButton',
    eventName: 'goto_amazon',
  },

  [GTM_EVENT_CATEGORY.DISTRIBUTION]: {
    event: 'puntiDistribuzione',
    eventName: 'punto_distribuzione',
    buttonName: 'punti di distribuzione',
  },

  [GTM_EVENT_CATEGORY.LOCATION_ACTIONS]: {
    event: `ricercaInsights`,
    eventName: `ricerca_piazza_insights`,
  },

  [GTM_EVENT_CATEGORY.EVENT_CLICKTHROUGH]: {
    event: `initiativeBtn`,
    eventName: `evento_nastrorosa`,
  },

  [GTM_EVENT_CATEGORY.PARTNER_CLICKTHROUGH]: {
    event: 'extBtn',
    eventName: 'partner_clicked',
  },

  [GTM_EVENT_CATEGORY.SUPPORT_US]: {
    event: 'formSostieni',
    eventName: 'sostienici_nastrorosa',
  },
} as const

const SECTION_REQUIRED_CATEGORIES = new Set([
  GTM_EVENT_CATEGORY.DONATION,
  GTM_EVENT_CATEGORY.VENDOR_CLICK,
])

/**
 * Invia un evento GTM con la configurazione appropriata
 * @param category - Categoria dell'evento GTM
 * @param additionalData - Dati aggiuntivi specifici per la categoria (es. section)
 * @throws Error se la configurazione è invalida o mancano dati richiesti
 */
export function dispatchGTMEvent<T extends GTM_EVENT_CATEGORY>(
  category: T,
  additionalData?: Partial<Omit<GTMEventMap[T], keyof GTMEventBase>>
): void {
  try {
    const config = GTM_CONFIG?.[category]

    if (!config) {
      throw new Error(`Invalid GTM configuration for category: ${category}`)
    }

    const needsSection = SECTION_REQUIRED_CATEGORIES.has(category)
    if (needsSection && !('section' in (additionalData || {}))) {
      throw new Error(`Section is required for ${category} events`)
    }

    const eventData = {
      ...config,
      ...additionalData,
    } as GTMEventMap[T]
    console.info('📡[GTM EVENT]:', {
      category,
      eventData,
    })

    sendGTMEvent(eventData)
  } catch (error) {
    console.error('❌[GTM ERROR]:', error)
    throw error
  }
}

// Example usage:
/*
dispatchGTMEvent(GTM_EVENT_CATEGORY.DONATION, { section: 'header' })
dispatchGTMEvent(GTM_EVENT_CATEGORY.NEWSLETTER)
*/
